import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { ConnectionService } from '@services/connection-service';
import { ConsultationSessionService } from '@services/consultation-session-service';

export const CONSULTATION_QUIZ_ROUTE = '/consultation-quiz';
const CHAT_V2_ROUTE = '/chatV2';
const CONSULTATION_SESSION_TYPE = 'consultationSession';

/** Validates /consultation-quiz/:id entries; ineligible users bounce to the legacy chat. */
export const consultationQuizGuard: CanActivateFn = async (route: ActivatedRouteSnapshot) => {
  const consultationSessionService = inject(ConsultationSessionService);
  const connectionService = inject(ConnectionService);
  const router = inject(Router);
  const objectId: string = route.params.id;
  if (!objectId) return router.createUrlTree(['/user'], { queryParams: { tab: 'home' } });
  const legacyChatUrlTree = router.createUrlTree([CHAT_V2_ROUTE, objectId], {
    queryParams: { ...route.queryParams, type: CONSULTATION_SESSION_TYPE },
  });
  if (!(await consultationSessionService.isUserEligibleForConsultationQuiz())) return legacyChatUrlTree;
  let session = consultationSessionService.getPendingConsultationSession();
  if (session?.id !== objectId) {
    try {
      [session] = await connectionService.fetchConsultationSessions({ where: { objectId } });
    } catch (error) {
      return legacyChatUrlTree;
    }
  }
  if (!session) return legacyChatUrlTree;
  if (!(await consultationSessionService.shouldOpenConsultationQuiz(session.get('PrivateMainConcernClass')))) {
    return legacyChatUrlTree;
  }
  consultationSessionService.setPendingConsultationSession(session);
  return true;
};

/** On /chatV2/:id — sends eligible consultation-session users to the quiz route. */
export const chatToConsultationQuizGuard: CanActivateFn = async (route: ActivatedRouteSnapshot) => {
  if (route.queryParams.type !== CONSULTATION_SESSION_TYPE) return true;
  if (route.queryParams.fallback) return true; // quiz explicitly handed this question off

  const consultationSessionService = inject(ConsultationSessionService);
  const connectionService = inject(ConnectionService);
  const router = inject(Router);
  const objectId: string = route.params.id;
  if (!objectId) return true;
  if (!(await consultationSessionService.isUserEligibleForConsultationQuiz())) return true;
  let session: Table.ConsultationSession;
  try {
    [session] = await connectionService.fetchConsultationSessions({ where: { objectId } });
  } catch (error) {
    return true;
  }
  if (!session) return true;
  consultationSessionService.setPendingConsultationSession(session);
  if (!(await consultationSessionService.shouldOpenConsultationQuiz(session.get('PrivateMainConcernClass')))) {
    return true;
  }
  return router.createUrlTree([CONSULTATION_QUIZ_ROUTE, objectId], { queryParams: route.queryParams });
};

/** On /regimen/chat?class=X — sends eligible classes to the quiz route. */
export const regimenChatToConsultationQuizGuard: CanActivateFn = async (route: ActivatedRouteSnapshot) => {
  const consultationSessionService = inject(ConsultationSessionService);
  const connectionService = inject(ConnectionService);
  const router = inject(Router);
  const concernClass: string = route.queryParams.class;
  if (!concernClass) return true;
  if (!(await consultationSessionService.shouldOpenConsultationQuiz(concernClass))) return true;
  let session: Table.ConsultationSession;
  try {
    session = await connectionService.findConsultationSession(concernClass);
  } catch (error) {
    return true;
  }
  if (!session) return true;
  consultationSessionService.setPendingConsultationSession(session);
  return router.createUrlTree([CONSULTATION_QUIZ_ROUTE, session.id]);
};
